<template>
  <!-- MODALE AJOUT/MODIFICATION -->
  <b-modal
    id="modal-eolienne-edit"
    size="xl"
    :title="`Éditer l'éolienne ` + getNomEolienneElseId(currentEolienne)"
    title-class="modal__title modal__title--edit"
    v-if="currentEolienne"
    @ok="isUpdateFrom ? putAerogenerateur() : postAerogenerateur()"
    @cancel="onCancel"
    @close="onCancel"
    no-close-on-esc
    no-close-on-backdrop
  >
    <b-form class="eolienne-form pt-2 px-md-3">
      <!--Eolienne-->
      <b-form-group
        label-cols-lg="3"
        label="Eolienne"
        label-size="lg"
        label-class="fw-bold pt-0"
        class="mb-0"
      >
        <b-form-group id="input-group-nomeolienne"
                      label="Nom de l'éolienne"
                      label-for="input-nomeolienne"
                      description="Le nom de l'éolienne doit correspondre à celui de l'arrêté préfectoral">
          <b-form-input
            id="input-nomeolienne"
            v-model="currentEolienne.nomEolienne"
            type="text"
            placeholder="Saisir le nom de l'éolienne"
          ></b-form-input>
        </b-form-group>
      </b-form-group>
      <!-- Constructeurs -->
      <b-form-group
        label-cols-lg="3"
        label="Constructeur"
        label-size="lg"
        label-class="fw-bold pt-0"
        class="mb-0"
      >
        <b-form-group
          id="input-group-constructeur"
          label="Constructeur *"
          label-for="input-constructeur"
          description="Veuillez sélectionner le constructeur de l’éolienne. Si le projet n’est pas encore définitif (avant le début de la construction), saisir les données au regard du scénario majorant en terme d’impact. Si le constructeur ne figure pas dans la liste, sélectionner « 00_constructeur non référencé »."
        >
          <b-form-select
            class="eolienne-form__select"
            :class="{
                'eolienne-form__select--autre': isSelectedConstructeurAutre
              }"
            :options="constructeurs"
            v-model="selectedConstructeur"
            @change="onChangeConstructeur()"
          ></b-form-select>
        </b-form-group>
        <b-form-group
          id="input-group-reference"
          label="Référence commerciale *"
          label-for="input-referenceCommercial"
          description="Veuillez sélectionner la référence commerciale. Si le projet n’est pas encore définitif (avant le début de la construction), saisir les données au regard du scénario majorant en terme d’impact. Si le modèle ne figure pas dans la liste, sélectionner « 00_référence commerciale non référencée »."
          v-show="showReferenceField"
        >
          <b-form-select
            class="eolienne-form__select"
            :class="{
                'eolienne-form__select--autre': isSelectedReferenceAutre
              }"
            :options="referencesModeles"
            v-model="selectedReference"
            @change="onChangeReference()"
          ></b-form-select>
        </b-form-group>
        <b-form-group
          id="input-group-constructeurAutre"
          label="Constructeur non référencé *"
          label-for="input-constructeurAutre"
          description="Veuillez saisir le nom du constructeur en étant particulièrement vigilant à l’exactitude de la saisie."
          v-show="showAutreConstructeurField"
        >
          <b-form-input
            id="input-constructeurAutre"
            v-model="currentEolienne.constructeurAutre"
            type="text"
            placeholder="Saisir un constructeur"
            :state="validConstructeurAutre"
          ></b-form-input>
          <b-form-invalid-feedback :state="validConstructeurAutre">
            Ce constructeur est déjà référencé.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          id="input-group-referenceAutre"
          label="Référence commerciale non référencée *"
          label-for="input-referenceAutre"
          description="Veuillez saisir le nom de la référence commerciale en étant particulièrement vigilant à l’exactitude de la saisie."
          v-show="showAutreReferenceField"
        >
          <b-form-input
            id="input-referenceAutre"
            v-model="currentEolienne.referenceAutre"
            type="text"
            placeholder="Saisir une référence"
          ></b-form-input>
        </b-form-group>
      </b-form-group>

      <!-- Caractéristiques -->
      <b-form-group
        label-cols-lg="3"
        label="Caractéristiques"
        label-size="lg"
        label-class="fw-bold pt-0"
        class="mb-0"
      >
        <!-- Puissance -->
        <b-form-group
          id="input-group-puissance"
          label="Puissance installée de l'éolienne *"
          label-for="input-puissance"
          :description="displayDescription(currentEolienne.puissance, 'Veuillez indiquer la puissance en MW.')"
        >
          <b-form-input
            id="input-puissance"
            v-model.number="currentEolienne.puissance"
            type="number"
            min="0"
            max="100"
            required
            placeholder="Saisir un nombre entre 0 et 100"
            :no-wheel="true"
            :state="validateField(currentEolienne.puissance, 0, 100)"
            @blur="validateField(currentEolienne.puissance, 0, 100)"
          ></b-form-input>
          <b-form-invalid-feedback :state="validateField(currentEolienne.puissance, 0, 100)">
            La puissance doit être comprise entre 0 et 100 MW.
          </b-form-invalid-feedback>
        </b-form-group>
      </b-form-group>

      <!-- Possède un allumage -->
      <b-form-group
        label-cols-lg="3"
        label="Possède un allumage"
        label-size="lg"
        label-class="fw-bold pt-0"
        class="mb-0"
      >
        <b-form-group label="Eolienne possédant un allumage">
          <b-form-checkbox
            id="checkbox-allumage"
            v-model="currentEolienne.isAllumage"
            @change="onChangeIsAllumage"
            name="checkbox-allumage"
            v-bind:value="true"
            v-bind:unchecked-value="false"
            class="mb-4"
          >
            <small class="text-muted">
              Veuillez cocher la case si l'éolienne possède une période d'allumage.
            </small>
          </b-form-checkbox>
        </b-form-group>
      </b-form-group>

      <!-- Période d'allumage -->
      <b-form-group
        v-show="currentEolienne.isAllumage"
        label-cols-lg="3"
        label="Période d'allumage"
        label-size="lg"
        label-class="fw-bold pt-0"
        class="mb-0"
      >
        <b-form-group label="Période d'allumage *">
          <b-form-radio-group
            title="Période d'allumage"
            id="btn-radios-periodeAllumage"
            v-model="currentEolienne.periodeAllumage"
            :options="periodesAllumage"
            button-variant="outline-primary"
            buttons
            name="radios-btn-periodeAllumage"
          ></b-form-radio-group>
        </b-form-group>
      </b-form-group>

      <!-- Type de feux -->
      <b-form-group
        v-show="currentEolienne.isAllumage"
        label-cols-lg="3"
        label="Type de feux"
        label-size="lg"
        label-class="fw-bold pt-0"
        class="mb-0"
      >
        <b-form-group label="Type de feux *">
          <b-form-radio-group
            title="Type de feux"
            id="btn-radios-type-feu"
            v-model="currentEolienne.typeFeu"
            :options="typesFeux"
            button-variant="outline-primary"
            buttons
            name="radios-btn-type-feu"
          ></b-form-radio-group>
        </b-form-group>
      </b-form-group>

      <!-- Gabarit -->
      <b-form-group
        label-cols-lg="3"
        label="Gabarit"
        label-size="lg"
        label-class="fw-bold pt-0"
        class="mb-0"
      >
        <b-form-group
          id="input-group-hauteurTotale"
          label="Hauteur totale  en bout de pale (m) *"
          label-for="input-hauteurTotale"
          :description="displayDescription(currentEolienne.hauteurTotale, 'Veuillez indiquer la hauteur  de l’éolienne en bout de pale, depuis le niveau du sol.')"
        >
          <b-form-input
            id="input-hauteurTotale"
            v-model.number="currentEolienne.hauteurTotale"
            type="number"
            min="0"
            max="500"
            required
            placeholder="Saisir un nombre entre 0 et 500"
            :no-wheel="true"
            :state="validateField(currentEolienne.hauteurTotale, 0, 500)"
            @blur="validateField(currentEolienne.hauteurTotale, 0, 500)"
          ></b-form-input>
          <b-form-invalid-feedback :state="validateField(currentEolienne.hauteurTotale, 0, 500)">
            La hauteur totale doit être comprise entre 0 et 500 m.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          id="input-group-coteNgf"
          label="Côte NGF au sol (m) *"
          label-for="input-coteNgf"
          :description="displayDescription(currentEolienne.coteNgf, 'Veuillez indiquer la côte NGF correspondant à l’emplacement du mât.')"
        >
          <b-form-input
            id="input-coteNgf"
            v-model.number="currentEolienne.coteNgf"
            type="number"
            max="5000"
            required
            placeholder="Saisir un nombre entre 0 et 5000"
            :no-wheel="true"
            :state="validateField(currentEolienne.coteNgf, 0, 5000)"
            @blur="validateField(currentEolienne.coteNgf, 0, 5000)"
          ></b-form-input>
          <b-form-invalid-feedback :state="validateField(currentEolienne.coteNgf, 0, 5000)">
            La côte NGF doit être comprise entre 0 et 5000 m.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          id="input-group-hauteurMatNacelle"
          label="Hauteur du mât + nacelle (m) *"
          label-for="input-hauteurMatNacelle"
          :description="displayDescription(currentEolienne.hauteurMatNacelle, 'Veuillez sélectionner la hauteur du haut de la nacelle, depuis le niveau du sol.')"
        >
          <b-form-input
            id="input-hauteurMatNacelle"
            v-model.number="currentEolienne.hauteurMatNacelle"
            type="number"
            min="0"
            max="500"
            required
            placeholder="Saisir un nombre entre 0 et 500"
            :no-wheel="true"
            :state="validateField(currentEolienne.hauteurMatNacelle, 0, 500)"
            @blur="validateField(currentEolienne.hauteurMatNacelle, 0, 500)"
          ></b-form-input>
          <b-form-invalid-feedback :state="validateField(currentEolienne.hauteurMatNacelle, 0, 500)">
            La hauteur du mât + nacelle doit être comprise entre 0 et 500 m.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          id="input-group-diametreRotor"
          label="Diamètre du rotor (m) *"
          label-for="input-diametreRotor"
          :description="displayDescription(currentEolienne.diametreRotor, 'Veuillez indiquer le diamètre du rotor.')"
        >
          <b-form-input
            id="input-diametreRotor"
            v-model.number="currentEolienne.diametreRotor"
            type="number"
            min="0"
            max="500"
            required
            placeholder="Saisir un nombre entre 0 et 500"
            :no-wheel="true"
            :state="validateField(currentEolienne.diametreRotor, 0, 500)"
            @blur="validateField(currentEolienne.diametreRotor, 0, 500)"
          ></b-form-input>
          <b-form-invalid-feedback :state="validateField(currentEolienne.diametreRotor, 0, 500)">
            Le diamètre du rotor doit être compris entre 0 et 500 m.
          </b-form-invalid-feedback>
        </b-form-group>
      </b-form-group>

      <div class="mt-4"></div>

      <!-- Situation -->
      <b-form-group
        label-cols-lg="3"
        label="Localisation"
        label-size="lg"
        label-class="fw-bold pt-0"
        class="mb-0"
      >
        <b-row>
          <b-col md="6">
            <b-form-group
              id="input-group-xaerogenerateur"
              :label="`Coordonnée X (${parc?.projection.libelle})*`"
              label-for="input-xaerogenerateur"
              class="mb-0"
            >
              <b-form-input
                id="input-xaerogenerateur"
                v-model.number="currentEolienne.xaerogenerateur"
                type="number"
                required
                step='.01'
                placeholder="Indiquer les coordonnées x"
                @input="$emit('on-input-coordinates', currentEolienne)"
                :no-wheel="true"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              id="input-group-yaerogenerateur"
              :label="`Coordonnée Y (${parc?.projection.libelle})*`"
              label-for="input-yaerogenerateur"
              class="mb-0"
            >
              <b-form-input
                id="input-yaerogenerateur"
                v-model.number="currentEolienne.yaerogenerateur"
                type="number"
                required
                step='.01'
                placeholder="Indiquer les coordonnées y"
                @input="$emit('on-input-coordinates', currentEolienne)"
                :no-wheel="true"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col class="small text-justify mt-1 mb-3">
            Veuillez indiquer les coordonnées du mât de l’éolienne, au format
            <span>{{ parc?.projection.libelle }}</span>. Assurez-vous d'avoir saisi le(s) commune(s) d'installation
            dans le formulaire du parc. Pour un mât déjà construit : les
            coordonnées correspondant à l’emplacement exact du mât sont
            attendus et doivent être privilégiées en cas de divergence avec
            les dossiers déposés auprès de l’administration (la position du
            curseur peut être directement ajustée sur la carte).
          </b-col>
        </b-row>
      </b-form-group>

      <!-- Alertes Map -->
      <div slot="image" class="edit-eolienne-map">
        <div class="edit-eolienne-map__alert">
          <div v-if="isLoadingCoordinatesValidation != null">
            <b-alert
              :model-value="true"
              class="rounded"
              variant="info"
              show
              v-if="isLoadingCoordinatesValidation"
            >
              <b-spinner small label="Small Spinner" type="grow"></b-spinner>
              <span class="ms-2">
                  Vérification des coordonnées saisies...
                </span>
            </b-alert>
            <b-alert
              :model-value="true"
              class="rounded"
              :variant="coordinatesAlert.state"
              show
              v-else
            >
              {{ coordinatesAlert.text }}
            </b-alert>
          </div>
        </div>
        <l-map :zoom="currentMapZoom" :center="currentMapCenter" @click="$emit('on-click-map', $event, currentEolienne)">
          <l-tile-layer
            :url="baseLayer.url"
            :attribution="baseLayer.attribution"
          ></l-tile-layer>
          <l-marker
            v-for="(marker, index) in markers"
            :key="index"
            draggable
            :lat-lng="marker"
            @drag="$emit('update-coordinates', $event, currentEolienne)"
            @dragstart="$emit('set-loading-validation')"
            @dragend="$emit('check-coordinates', currentEolienne)"
            @click="$emit('remove-marker', index, currentEolienne)"
          ></l-marker>

          <l-polygon
            v-for="(communeGeom, index) in communeGeoms"
            :key="'polygon' + index"
            :lat-lngs="communeGeom"
            :color="color"
            fillColor="#3388ff"
          ></l-polygon>
        </l-map>
      </div>
    </b-form>
    <template #footer="{ ok, cancel }">
      <!-- Emulate built in modal footer ok and cancel button actions -->
      <span
        class="d-inline-block"
        tabindex="0"
        id="eolienne-edit-btn"
      >
          <b-tooltip target="#eolienne-edit-btn">
            {{ disableSubitButtun ? "Veuillez renseigner les champs obligatoires (*)" : '' }}
          </b-tooltip>
          <b-button
            variant="info"
            :title="
              isUpdateFrom
                ? 'Enregistrer les modifications'
                : 'Enregistrer la nouvelle éolienne'
            "
            :disabled="disableSubitButtun"
            @click="ok()"
          >
            Valider
          </b-button>
        </span>
      <b-button
        variant="default"
        title="Annuler les modifications"
        @click="cancel()"
      >
        Annuler
      </b-button>
    </template>
  </b-modal>

</template>
<script setup>
import { LMap, LTileLayer, LMarker, LPolygon } from "@vue-leaflet/vue-leaflet";
import {
  BModal, BButton, BCol, BRow, BForm,
  BFormGroup, BFormInput, BFormSelect, BFormCheckbox,
  BFormRadioGroup, BFormInvalidFeedback, BAlert, BSpinner, BTooltip
} from "bootstrap-vue-next";

import {useReactiveStoreGetters} from "@/composables/useStore";
import {computed, onMounted, ref, watch} from "vue";
import {aerogenerateurService} from "@/service/aerogenerateur.service";
import {lexiqueService} from "@/service/lexique.service";
import {collectiviteService} from "@/service/collectivite.service";
import proj4 from "proj4";
import {mapService} from "@/service/map.service";

// Lifecycle hook
onMounted(() => {
  init()
})

// Émissions
const emit = defineEmits([
  'reload-parc',
  'add-marker',
  'remove-marker',
  'set-loading-validation',
  'update-coordinates',
  'on-input-coordinates',
  'check-coordinates',
  'on-click-map',
])

// Props
const {
  eolienne,
  coordinatesAlert,
  mapZoom,
  mapCenter,
  markers,
  addNotification,
  typesFeux
} = defineProps({
  getNomEolienneElseId: {
    type: Function,
    required: true,
  },
  mapZoom: {
    type: Number,
    required: true,
  },
  mapCenter: {
    required: true,
  },
  eolienne: {
    type: [Object, null],
    required: true,
  },
  markers: {
    type: Array,
    required: true,
  },
  coordinatesAlert: {
    type: Object,
    required: true,
  },
  isLoadingCoordinatesValidation: {
    type: [Boolean, null],
    required: true,
  },
  typesFeux: {
    type: Array,
    required: true,
  },
  addNotification: {
    type: Function,
    required: true,
  },
  isUpdateFrom: {
    type: Boolean,
    required: true,
  }
})

// Getters
const {
  parc,
  baseLayer,
  isInventaireCommunes,
  epsg,
} = useReactiveStoreGetters([
  "parc",
  "baseLayer",
  "isInventaireCommunes",
  "epsg",
]);


// Data
const selectedConstructeur = ref({})
const selectedReference = ref({})
const constructeurs = ref([])
const referencesModeles = ref([])
const periodesAllumage = ref([])
const communeGeoms = ref([])
const color = ref("#FFFFCC")

const currentEolienne = ref(eolienne)

// Watch for changes in the prop
watch(() => eolienne, (newValue) => {
  currentEolienne.value = { ...newValue };
  getDefaultValues();
})

// Computed
const isSelectedConstructeurAutre = computed(() => selectedConstructeur.value === "autre");
const isSelectedReferenceAutre = computed(() => selectedReference.value === "autre");
const showReferenceField = computed(() => selectedConstructeur.value.nom != null);
const showAutreConstructeurField = computed(() => isSelectedConstructeurAutre.value);

const showAutreReferenceField = computed(() =>
  isSelectedConstructeurAutre.value || isSelectedReferenceAutre.value
);

const isReferenceAutre = computed(() =>
  currentEolienne.value.referenceAutre != null &&
  currentEolienne.value.referenceAutre !== ""
);

const isConstructeurAutre = computed(() =>
  currentEolienne.value.constructeurAutre != null &&
  currentEolienne.value.constructeurAutre !== ""
);

const validConstructeurAutre = computed(() => {
  if (!isSelectedConstructeurAutre.value) {
    return null;
  }

  const constructeurIndex = constructeurs.value.findIndex(constructeurEL => {
    if (
      formatInput(constructeurEL.text) ===
      formatInput(currentEolienne.value.constructeurAutre)
    ) {
      return true;
    }
  });
  return constructeurIndex === -1 ? null : false;
});

const validConstructeurAndReference = computed(() =>
  (currentEolienne.value?.referenceModele != null &&
    !isSelectedReferenceAutre.value &&
    currentEolienne.value?.constructeur != null &&
    !isSelectedConstructeurAutre.value) ||
  (currentEolienne.value?.constructeur != null &&
    !isSelectedConstructeurAutre.value &&
    isSelectedReferenceAutre.value &&
    isReferenceAutre.value) ||
  (isSelectedConstructeurAutre.value &&
    isReferenceAutre.value &&
    isConstructeurAutre.value)
);

const validCoordinates = computed(() => {
  return coordinatesAlert.state === "success";
});

const disableSubitButtun = computed(() =>
  validCoordinates.value === false ||
  validConstructeurAndReference.value === false ||
  validConstructeurAutre.value === false ||
  !validateField(currentEolienne.value?.puissance, 0, 100) ||
  !validateField(currentEolienne.value?.hauteurTotale, 0, 500) ||
  !validateField(currentEolienne.value?.coteNgf, 0, 5000) ||
  !validateField(currentEolienne.value?.hauteurMatNacelle, 0, 500) ||
  !validateField(currentEolienne.value?.diametreRotor, 0, 500)
);

const wgs64FromXY = computed(() => {
  if (currentEolienne.value &&
    currentEolienne.value.xaerogenerateur !== null &&
    currentEolienne.value.xaerogenerateur !== undefined &&
    currentEolienne.value.yaerogenerateur !== null &&
    currentEolienne.value.yaerogenerateur !== undefined
  ) {
    return proj4(`EPSG:${epsg.value}`).inverse([
      parseFloat(currentEolienne.value.xaerogenerateur),
      parseFloat(currentEolienne.value.yaerogenerateur)
    ])
  }
  return null
});

const currentMapCenter = computed(() => {
  if (markers && markers.length > 0) {
    return [markers[0][0], markers[0][1]];
  } else if (wgs64FromXY.value) {
    return [wgs64FromXY.value[1], wgs64FromXY.value[0]];
  } else if (communeGeoms.value && communeGeoms.value[0]) {
    return mapService.getMultipolygonCenter(communeGeoms.value[0])
  }
  return mapCenter;
});

const currentMapZoom = computed(() => {
  if ((currentEolienne.value &&
      currentEolienne.value.xaerogenerateur &&
      currentEolienne.value.yaerogenerateur) ||
    (communeGeoms.value && communeGeoms.value.length > 0)) {
    return 13;
  }
  return mapZoom;
});

// Methods
const validateField = (value, min, max) => {
  if (!!value || value === 0) {
    return value >= min && (max === undefined || value <= max);
  }
  return null;
};

const formatInput = (input) => {
  if (!input) {
    return null;
  }
  return input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replaceAll("-", " ")
    .toUpperCase();
};

const onCancel = () => {
  emit('reload-parc');
};

const getReferencesModeles = (constructeurId) => {
  referencesModeles.value = [];
  aerogenerateurService
    .getReferencesModeles(constructeurId)
    .then(response => {
      if (response) {
        referencesModeles.value = response.map(reference => {
          return { value: reference, text: reference.referenceCommerciale };
        });
        referencesModeles.value.push({
          value: "autre",
          text: "00_référence commerciale non référencée"
        });
      }
    })
    .catch(err => {
      console.log(err);
    });
};
const onChangeReference = () => {
  currentEolienne.value.referenceAutre = null;
  if (showAutreReferenceField.value) {
    currentEolienne.value.referenceModele = null;
    currentEolienne.value.constructeur = selectedConstructeur.value;
  } else {
    currentEolienne.value.referenceModele = selectedReference.value;
    currentEolienne.value.constructeur = selectedConstructeur.value;
  }
};

const onChangeCheckbox = (isChecked) => {
  if (isChecked) {
    onChangeReference();
    currentEolienne.value.constructeurAutre = null;
    currentEolienne.value.referenceAutre = null;
  } else {
    currentEolienne.value.referenceModele = null;
  }
};

const onChangeIsAllumage = (event) => {
  const isAllumage = event.target.checked;
  if (isAllumage && !currentEolienne.value.periodeAllumage) {
    currentEolienne.value.periodeAllumage = periodesAllumage.value[0].value;
    currentEolienne.value.typeFeu = typesFeux[0].value;
  }
};

const onChangeConstructeur = () => {
  currentEolienne.value.referenceModele = null;
  currentEolienne.value.constructeurAutre = null;
  currentEolienne.value.referenceAutre = null;
  selectedReference.value = {};
  if (showReferenceField.value) {
    currentEolienne.value.constructeur = selectedConstructeur.value;
    getReferencesModeles(selectedConstructeur.value.id);
  } else {
    currentEolienne.value.constructeur = null;
    selectedReference.value = {};
  }
};

const displayDescription = (value, description) => {
  return !!value ? '' : description;
};

// Modifiez la méthode setGeomWgs84FromXY pour utiliser le computed property
const setGeomWgs84FromXY = () => {
  if (wgs64FromXY.value) {
    currentEolienne.value.geomWgs84 = {
      type: "Point",
      coordinates: [wgs64FromXY.value[0], wgs64FromXY.value[1]]
    };
  }
};


const putAerogenerateur = () => {
  setGeomWgs84FromXY();

  aerogenerateurService
    .putAerogenerateur(currentEolienne.value)
    .then(response => {
      const httpMethod = "PUT";
      addNotification(response, httpMethod);
    })
    .catch(err => {
      console.log(err);
    });
};

const postAerogenerateur = () => {
  setGeomWgs84FromXY();

  aerogenerateurService
    .postAerogenerateur(currentEolienne.value)
    .then(response => {
      const httpMethod = "POST";
      addNotification(response, httpMethod);
    })
    .catch(err => {
      console.log(err);
    });
};

const getSelectOptions = () => {
  Promise.all([
    aerogenerateurService.getConstructeurs(),
    lexiqueService.getLexPeriodeAllumage()
  ])
    .then(response => {
      constructeurs.value = response[0].map(constructeur => {
        return { value: constructeur, text: constructeur.nom };
      });
      constructeurs.value.push({
        value: "autre",
        text: "00_constructeur non référencé"
      });
      periodesAllumage.value = response[1].map(type => {
        return { value: type, text: type.libelle };
      });
    })
    .catch(err => {
      console.log(err);
    });
};

const addPolygonCommunesParc = () => {
  if (parc.value && isInventaireCommunes.value) {
    communeGeoms.value = [];
    //Récupération des communes du parc
    collectiviteService
      .getCommuneGeomByParc(parc.value)
      .then(data => {
        if (data) {
          const polygons = [];
          //Transformation des coordonées en tableau de long, lat
          data.forEach(communeJson => {
            communeJson.geomWgs84.coordinates.forEach(communeFragment => {
              const polygon = communeFragment[0].map(
                point => {
                  return [point[1], point[0]];
                }
              );
              polygons.push(polygon);
            })
          });
          communeGeoms.value = polygons;
        }
      })
      .catch(err => {
        console.log(err);
      });
  }
};

const getDefaultValues = () => {
  // Pré-selection des input select avec les valeurs de l'objet Eolienne
  if (currentEolienne.value?.constructeur != null) {
    selectedConstructeur.value = currentEolienne.value.constructeur;
    getReferencesModeles(currentEolienne.value.constructeur.id);
    if (currentEolienne.value.referenceModele != null) {
      selectedReference.value = currentEolienne.value.referenceModele;
    } else {
      selectedReference.value = "autre";
    }
  } else {
    selectedConstructeur.value = "autre";
  }
}

const init = async () => {
  // mapService.setDefaultIcons();
  await getSelectOptions();
  getDefaultValues();
  addPolygonCommunesParc();

};

</script>
<style lang="scss">
.eolienne-form {
  .form-control {
    border: #ced4da 1px solid;
  }

  &__title {
    font-size: 1.6rem;
    margin-bottom: 0;
    margin-left: 3px;
  }

  &__select {
    option:last-child {
      font-style: italic;
    }

    &--autre {
      font-style: italic;
      option:not([value="autre"]) {
        font-style: normal;
      }
    }
  }

  &__checkbox > * {
    cursor: pointer;
  }

  .edit-eolienne-map {
    height: 400px;
    position: relative;

    &__alert {
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 1000;
      margin-left: 55px;
    }
  }
}

</style>
